import type { MatchState, PlayerState } from './state';
import type { PlayerInput } from '../types';
import type { SimConfig } from '../config/simConfig';
import type { KickRequest } from './ballActions';
import { clonePlayer } from './clone';
import { applyActions } from './actions';
import { integratePlayers } from './movement';
import { finalizePlayer } from './step';
import { fenceCagePlayer } from './bounds';

/**
 * Prediction subset (§5.1) — advance ONE entity, treating all others as kinematic ghosts. Runs the
 * §5.1 order restricted to the entity: applyActions → integrate → finalize (possession/collisions
 * don't change an isolated entity's pos/vel/action). PURE — lives in shared, inside the purity
 * boundary. Parity law (T-SIM-003): when all other entities are static and out of contact range,
 * `stepEntity(id)` produces the same pos/vel/action as `step()` does for that entity.
 */
export function stepEntity(state: MatchState, id: number, input: PlayerInput, cfg: SimConfig): PlayerState {
  const p = clonePlayer(state.players[id]!);
  // D62: the spread is SHALLOW, so `mini.ball` was the CALLER'S ball — and applyActions writes through
  // it (the GK_HOLD distribution branch repositions the ball and latches gkLock). That is a straight
  // violation of invariant 1 ("step() never mutates args"): the client's prediction scratch ball was
  // being teleported and its gkLock latched permanently, after which no keeper pickup could ever be
  // predicted again. Isolate every field applyActions can write.
  const mini: MatchState = {
    ...state,
    players: [p],
    ball: { ...state.ball, pos: { ...state.ball.pos }, vel: { ...state.ball.vel }, spin: { ...state.ball.spin } },
    attackDir: [...state.attackDir] as [1 | -1, 1 | -1],
    restart: state.restart ? { ...state.restart, spot: { ...state.restart.spot } } : null,
    referee: { ...state.referee, pos: { ...state.referee.pos } },
  };
  const inputs: PlayerInput[] = [];
  inputs[id] = input;
  const kicks: KickRequest[] = [];
  applyActions(mini, inputs, cfg, kicks);
  integratePlayers(mini, inputs, cfg);
  fenceCagePlayer(p, state.mode.teamSize, cfg); // D40 cage boards — parity with reflectCageWalls
  finalizePlayer(p, input, cfg);
  return p;
}
